import { type OpRecord } from '@dl/protocol';
import {
  FactPayloadSchema,
  foldFactChainFull,
  type FactPayload,
} from '@dl/shared';

import { type SearchSqlBackend } from './sql.js';
import {
  type CandidateRef,
  type SearchLane,
  type SearchScope,
} from './types.js';

/**
 * FTS5 virtual table over text-valued facts (I.E.3 / #40).
 *
 * Schema parity is enforced by mirrored migrations:
 *   - apps/do-workspace/migrations/0003_fts.sql
 *   - packages/replica/src/schema.ts (version 6)
 *
 * Tokenizer: `unicode61 remove_diacritics 2` — the diacritic-folding
 * variant that handles "café" ↔ "cafe" without ICU dependencies. Emoji
 * and CJK characters round-trip via `unicode61` (each codepoint becomes
 * its own token); the test suite confirms.
 *
 * `fact_id UNINDEXED` lets us cheaply join back to predicate_head when
 * a downstream surface wants the chain head. At Phase 1 scope the FTS
 * row carries the entity id + predicate so the search lane can return
 * `CandidateRef` without further hydration (hydration of the value
 * happens at the merge step via projection — see SearchRouter).
 *
 * The FTS table indexes ONLY facts whose value_kind is `text` or
 * `rich_text` (per AC #3 of #40 — `'text'` value-kind facts; we treat
 * `rich_text` the same way because the substrate stores it as a string).
 * Number/boolean/ref/etc. facts are not indexed by FTS — they go through
 * the graph lane or future field-specific indices.
 */
export const FACT_FTS_TABLE = 'fact_fts';
export const FACT_FTS_DDL = `
  CREATE VIRTUAL TABLE IF NOT EXISTS ${FACT_FTS_TABLE}
    USING fts5(
      fact_id    UNINDEXED,
      entity_id  UNINDEXED,
      predicate  UNINDEXED,
      text_value,
      tokenize = 'unicode61 remove_diacritics 2'
    );
`;

/**
 * Insert or replace one fact's FTS row. The fact_id key is
 * `entity_id|predicate` so a `fact.edit` upsert replaces the prior row
 * (FTS5 doesn't expose ON CONFLICT — we DELETE + INSERT inside the same
 * statement batch for atomicity inside the DO turn).
 */
export function upsertFactFts(
  sql: SearchSqlBackend,
  entity_id: string,
  predicate: string,
  text_value: string,
): void {
  const fact_id = `${entity_id}|${predicate}`;
  sql.exec(`DELETE FROM ${FACT_FTS_TABLE} WHERE fact_id = ?;`, fact_id);
  sql.exec(
    `INSERT INTO ${FACT_FTS_TABLE} (fact_id, entity_id, predicate, text_value)
       VALUES (?, ?, ?, ?);`,
    fact_id,
    entity_id,
    predicate,
    text_value,
  );
}

/** Drop one fact's FTS row (used on soft-delete or non-text edit). */
export function deleteFactFts(
  sql: SearchSqlBackend,
  entity_id: string,
  predicate: string,
): void {
  const fact_id = `${entity_id}|${predicate}`;
  sql.exec(`DELETE FROM ${FACT_FTS_TABLE} WHERE fact_id = ?;`, fact_id);
}

/**
 * Rebuild the FTS table from the op log. Used by the replay test
 * (B5 / #10). The fold collapses each (subject, predicate) chain via
 * `foldFactChainFull`; only string-valued, non-soft-deleted facts are
 * indexed. Non-string values are skipped (the predicate registry
 * filters by value_kind at write time once it lands; until then this
 * defensive filter keeps the index well-formed).
 */
export function rebuildFactFts(
  sql: SearchSqlBackend,
  ops: Iterable<OpRecord<unknown>>,
): void {
  sql.exec(`DELETE FROM ${FACT_FTS_TABLE};`);

  const chains = new Map<string, { subject: string; predicate: string; chain: FactPayload[] }>();
  for (const op of ops) {
    const payload = op.payload as { kind?: unknown } | null | undefined;
    if (!payload || typeof payload !== 'object') continue;
    const kind = (payload as { kind?: unknown }).kind;
    if (typeof kind !== 'string' || !kind.startsWith('fact.')) continue;
    const parsed = FactPayloadSchema.parse(payload);
    const key = `${op.subject}|${op.predicate}`;
    const existing = chains.get(key);
    if (existing) existing.chain.push(parsed);
    else chains.set(key, { subject: op.subject, predicate: op.predicate, chain: [parsed] });
  }

  for (const { subject, predicate, chain } of chains.values()) {
    const folded = foldFactChainFull(chain);
    if (!folded) continue;
    if (folded.soft_deleted) continue;
    if (typeof folded.value !== 'string') continue;
    if (folded.value.length === 0) continue;
    upsertFactFts(sql, subject, predicate, folded.value);
  }
}

/**
 * The FTS-lane SearchLane. Quotes the user query as a single phrase
 * (so a stray `OR` from the user isn't interpreted as an FTS operator)
 * and uses `MATCH` against the `text_value` column.
 */
export function ftsLane(sql: SearchSqlBackend): SearchLane {
  return {
    name: 'fts',
    weight: 0.8, // slight discount vs. exact-name matches — names are higher signal
    query(q: string, scope: SearchScope): CandidateRef[] {
      const phrase = ftsEscape(q);
      if (phrase.length === 0) return [];
      const limit = scope.per_lane_limit ?? 50;

      // bm25() returns a non-positive ranking score (closer to 0 is
      // better). We invert it to get a positive lane_score (lower bm25
      // → higher lane_score).
      const rows = sql.all<{ entity_id: string; predicate: string; rank: number }>(
        `SELECT entity_id, predicate, bm25(${FACT_FTS_TABLE}) AS rank
           FROM ${FACT_FTS_TABLE}
          WHERE ${FACT_FTS_TABLE} MATCH ?
          ORDER BY rank ASC
          LIMIT ?;`,
        phrase,
        limit,
      );

      return rows.map((row) => ({
        kind: 'fact',
        entity_id: row.entity_id,
        predicate: row.predicate,
        lane_name: 'fts',
        // Negate so larger is better. bm25 typically yields ~[-20, 0];
        // shift+scale into roughly [0.5, 2.5].
        lane_score: Math.max(0.5, 1 + -row.rank / 20),
      }));
    },
  };
}

/**
 * Quote the user query as an FTS5 phrase. Double quotes inside the
 * query are escaped by doubling per the FTS5 grammar; the whole thing
 * is wrapped in double quotes so FTS operators (`AND`, `OR`, `NOT`,
 * `NEAR`, `*`) are treated as literal tokens.
 */
export function ftsEscape(q: string): string {
  const trimmed = q.trim();
  if (trimmed.length === 0) return '';
  const escaped = trimmed.replace(/"/g, '""');
  return `"${escaped}"`;
}
